import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { swSettings } from "app/common/services/swSettings";
import { isOnPage, CI_AND_SI_PAGE_COLLECTION } from "app/components/PageTopBanner/pageCollections";
import { useShowDiscountPopup } from "app/components/React/TrialBanner/DiscountBanner";
import { getIsInDiscount } from "app/components/timers/utils/helpers";
import {
    EStepState,
    WizardRole,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { useActivationWizardSteps } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/UseActivationWizardSteps";
import { isNewHomepage } from "app/pages/digital-suite/home-page/helpers";
import {
    isAccountCreatedToday,
    isTraditionalTrialOrAfterTrialUser,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export const TrialBannerDict = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    DISCOUNT_BANNER: "discountBanner",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    TRIAL_BANNER: "trialBanner",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NONE: "none",
} as const;

export type TrialBannerType = (typeof TrialBannerDict)[keyof typeof TrialBannerDict];

const STICKY_PAGES = ["activation", "track"];

interface IUseTrialBannerApi {
    trialBannerType: TrialBannerType;
    isSticky: boolean;
    hasTrialBanner: boolean;
    isInDiscount: boolean;
    displayHomepageButton: boolean;
    shouldGoToAfterTrial: boolean;
    isAccountCreatedToday: boolean;
    canPurchaseNoTouch: boolean;
}

export const useTrialBanner = (): IUseTrialBannerApi => {
    const {
        state: { parent, name },
    } = useCurrentStateAndParams();

    const {
        components: {
            Home: {
                resources: { CanPurchaseNoTouch: canPurchaseNoTouch },
            },
        },

        user: {
            customData: { quiz },
        },
    } = swSettings;
    const shouldShowBanner = isTraditionalTrialOrAfterTrialUser(swSettings) && !proNavigator.isSalesPremiumFeatures();
    const { shouldShowDiscountPopup, discountEndTime } = useShowDiscountPopup(swSettings);
    const isInDiscount = getIsInDiscount({
        discountEndDateFromSettings: swSettings.user?.customData?.DiscountEndDate,
        discountEndTime,
    });

    const wizardRole = WizardRole[quiz?.DepartmentType as keyof typeof WizardRole] || WizardRole.General;

    const { stepsState } = useActivationWizardSteps(wizardRole);

    const shouldShowDiscountContent = shouldShowDiscountPopup || isInDiscount;

    const trialBannerType = useMemo((): TrialBannerType => {
        if (!shouldShowBanner) {
            return TrialBannerDict.NONE;
        }

        return shouldShowDiscountContent ? TrialBannerDict.DISCOUNT_BANNER : TrialBannerDict.TRIAL_BANNER;
    }, [shouldShowDiscountContent, shouldShowBanner]);

    const hasTrialBanner = useMemo(() => {
        if (trialBannerType === TrialBannerDict.NONE) {
            return false;
        }

        if (parent === "digitalsuite_marketresearch" && (!isNewHomepage() || name !== "digitalsuite-home")) {
            return true;
        }

        const showOnCurrentPage = CI_AND_SI_PAGE_COLLECTION.some((p) => isOnPage({ current: name, page: p }));
        if (showOnCurrentPage) {
            return true;
        }

        return false;
    }, [name, parent, trialBannerType]);

    const displayHomepageButton = useMemo(() => {
        if (isNewHomepage()) {
            return stepsState?.some((step) => step?.state === EStepState.INCOMPLETE);
        }

        return false;
    }, [stepsState]);

    return {
        trialBannerType,
        isSticky: STICKY_PAGES.includes(parent ?? ""),
        hasTrialBanner,
        isInDiscount,
        displayHomepageButton,

        shouldGoToAfterTrial: quiz?.shouldGoToAfterTrial ?? false,
        isAccountCreatedToday: isAccountCreatedToday(swSettings),
        canPurchaseNoTouch: !!canPurchaseNoTouch,
    };
};
